import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, ChevronLeft, ChevronRight, Layers, Search } from 'lucide-react'
import { expiryCode } from '@apna/shared'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { payoffTradeLabel, payoffTradeName } from './heldPayoff'
import type { OpenBookTrade } from './useOpenBook'

const PAGE_SIZE = 10
/*
 * ONE RESOLVER, NOT A THIRD COPY. This file used to restate the precedence
 * itself and got it wrong in the same way `payoffTradeLabel` did — see its
 * header for the trade that listed itself as one of its own contracts.
 */
const nameOf = payoffTradeName
const timeOf = (t: OpenBookTrade) => new Date(t.openedAt).toLocaleTimeString('en-IN', {
  timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
})

/** Search all trades, render ten at a time. Scrolling stays inside this popup, never the chart. */
export function PayoffTradePicker({ trades, selected, onSelect }: {
  trades: readonly OpenBookTrade[]
  selected: OpenBookTrade | undefined
  onSelect: (tradeId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  const [active, setActive] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const listId = useId()
  const entries = useMemo(() => {
    const occurrences = new Map<string, number>()
    return trades.map((trade) => {
      const label = payoffTradeLabel(trade)
      const entry = (occurrences.get(label) ?? 0) + 1
      occurrences.set(label, entry)
      return { trade, entry, search: (label + ' ' + trade.underlying + ' ' + trade.legs.map((l) => l.symbol).join(' ')).toLowerCase() }
    })
  }, [trades])
  const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean)
  const matches = entries.filter((entry) => terms.every((term) => entry.search.includes(term)))
  const pages = Math.max(1, Math.ceil(matches.length / PAGE_SIZE))
  const currentPage = Math.min(page, pages - 1)
  const visible = matches.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE)
  const activeIndex = Math.min(active, Math.max(0, visible.length - 1))
  const choose = (id: string) => { onSelect(id); setOpen(false) }
  useEffect(() => {
    if (open) document.getElementById(listId + '-' + activeIndex)?.scrollIntoView?.({ block: 'nearest' })
  }, [open, listId, activeIndex, currentPage, query])

  return (
    <Popover open={open} onOpenChange={(next) => {
      setOpen(next)
      if (next) { setQuery(''); setPage(0); setActive(0) }
    }}>
      <PopoverTrigger aria-label="Open trade" disabled={trades.length === 0} data-testid="payoff-trade-select"
        className="group flex h-9 w-full min-w-0 items-center gap-2.5 rounded-md border border-border bg-secondary/40 px-3 text-left transition-colors hover:border-primary/40 hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50">
        <Layers size={14} className="shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate text-xs font-semibold" data-testid="payoff-dock-title">
          {selected ? (selected.strategyName ? selected.underlying + ' · ' : '') + nameOf(selected) : 'Choose an open trade'}
        </span>
        {selected && <span className="hidden shrink-0 text-[11px] text-muted-foreground md:inline">{selected.broker.label} · {timeOf(selected)}</span>}
        <ChevronDown size={13} className="shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </PopoverTrigger>
      <PopoverContent align="start" className="z-[80] w-[min(480px,calc(100vw-24px))] rounded-lg p-0 shadow-xl"
        onOpenAutoFocus={(event) => { event.preventDefault(); input.current?.focus() }}>
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <Search size={14} className="shrink-0 text-muted-foreground" />
          <input ref={input} value={query} role="combobox" aria-label="Search open trades" aria-expanded={open}
            aria-controls={listId} aria-autocomplete="list" aria-activedescendant={visible.length ? listId + '-' + activeIndex : undefined}
            placeholder="Search strategy, strike, symbol or account…" className="h-6 min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/70"
            onChange={(event) => { setQuery(event.target.value); setPage(0); setActive(0) }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault()
                setActive((n) => Math.max(0, Math.min(visible.length - 1, n + (event.key === 'ArrowDown' ? 1 : -1))))
              }
              if (event.key === 'Enter' && visible[activeIndex]) { event.preventDefault(); choose(visible[activeIndex]!.trade.tradeId) }
            }} />
          <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">{trades.length}</span>
        </div>
        <div id={listId} role="listbox" aria-label="Open trades" className="max-h-[min(360px,calc(100dvh-220px))] overflow-y-auto overscroll-contain p-1" data-testid="payoff-trade-list">
          {visible.length === 0 && <p className="px-3 py-8 text-center text-xs text-muted-foreground">No matching trades</p>}
          {visible.map(({ trade, entry }, i) => {
            const held = trade.legs.filter((l) => l.netQty !== 0)
            return (
              <button key={trade.tradeId} id={listId + '-' + i} type="button" role="option" tabIndex={-1}
                aria-selected={selected?.tradeId === trade.tradeId} title={payoffTradeLabel(trade)}
                onMouseEnter={() => setActive(i)} onClick={() => choose(trade.tradeId)}
                className={cn('flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors',
                  i === activeIndex ? 'bg-secondary' : 'hover:bg-secondary/60', selected?.tradeId === trade.tradeId && 'text-primary')}>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-xs font-semibold">{nameOf(trade)}</span>
                    <span className="shrink-0 rounded bg-secondary px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">{trade.mode}</span>
                    {entry > 1 && <span className="shrink-0 text-[10px] text-muted-foreground">Entry {entry}</span>}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground">
                    <span>{trade.broker.label}</span><span>·</span><span>{trade.underlying} {expiryCode(held[0]?.expiry ?? null)}</span>
                    <span>·</span><span>{held.length} leg{held.length === 1 ? '' : 's'}</span><span>·</span><span>{timeOf(trade)}</span>
                  </span>
                </span>
                {selected?.tradeId === trade.tradeId && <Check size={14} className="shrink-0 text-primary" />}
              </button>
            )
          })}
        </div>
        <div className="flex items-center justify-between border-t border-border px-3 py-2 text-[10px] text-muted-foreground">
          <span role="status">{matches.length ? currentPage * PAGE_SIZE + 1 : 0}–{Math.min((currentPage + 1) * PAGE_SIZE, matches.length)} of {matches.length} trades</span>
          <div className="flex items-center gap-1">
            <button type="button" aria-label="Previous trades" disabled={currentPage === 0} onClick={() => { setPage(currentPage - 1); setActive(0) }} className="rounded p-1 hover:bg-secondary disabled:opacity-30"><ChevronLeft size={14} /></button>
            <span className="px-1 tabular-nums">{currentPage + 1} / {pages}</span>
            <button type="button" aria-label="Next trades" disabled={currentPage + 1 >= pages} onClick={() => { setPage(currentPage + 1); setActive(0) }} className="rounded p-1 hover:bg-secondary disabled:opacity-30"><ChevronRight size={14} /></button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
